import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { db } from '../lib/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaWhatsapp, FaGoogle, FaLayerGroup, FaStar } from 'react-icons/fa';

const { FiEdit2, FiAlertTriangle, FiCheckSquare, FiSquare } = FiIcons;

const EditSetup = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState('direct');
  const [url, setUrl] = useState('');
  
  // Review Filter State
  const [reviewFilterEnabled, setReviewFilterEnabled] = useState(false);
  const [googleReviewLink, setGoogleReviewLink] = useState('');
  const [dummyPageMessage, setDummyPageMessage] = useState('');

  const [profile, setProfile] = useState({
    company: '',
    logoUrl: '',
    bio: '',
    links: []
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!state?.tag_code) {
      navigate('/editqr');
      return;
    }

    const init = async () => {
      // Initialize Review Filter Settings
      setReviewFilterEnabled(state.negative_review_filter_enabled === 1);
      setGoogleReviewLink(state.google_review_link || '');
      setDummyPageMessage(state.dummy_page_message || '');

      if (state.link_type === 'smart') {
        setMode('smart');
        setProfile(prev => ({
          ...prev,
          company: state.company_name || '',
          logoUrl: state.logo_url || '',
          bio: state.bio || ''
        }));
        
        const links = await db.getProfileLinks(state.tag_code);
        const paddedLinks = [...links];
        while (paddedLinks.length < 6) {
          paddedLinks.push({ label: '', url: '' });
        }
        setProfile(prev => ({ ...prev, links: paddedLinks }));
      } else {
        setMode('direct');
        setUrl(state.redirect_url || '');
        setProfile(prev => ({ ...prev, links: Array(6).fill({ label: '', url: '' }) }));
      }
    };

    init();
  }, [state, navigate]);

  const updateLink = (index, field, value) => {
    const newLinks = [...profile.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setProfile(prev => ({ ...prev, links: newLinks }));
  };

  const validateUrl = (inputUrl) => {
    if (!inputUrl) return null;
    let finalUrl = inputUrl.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) finalUrl = 'https://' + finalUrl;
    return finalUrl;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      const updates = { is_active: 1 };
      
      // Save Review Filter Settings regardless of mode, but mostly used in Direct
      updates.negative_review_filter_enabled = reviewFilterEnabled ? 1 : 0;
      
      if (reviewFilterEnabled) {
        const validGoogleLink = validateUrl(googleReviewLink);
        if (!validGoogleLink) throw new Error("Please enter a valid Google Review Link.");
        updates.google_review_link = validGoogleLink;
        updates.dummy_page_message = dummyPageMessage;
      } else {
        // Optional: clear these if disabled, or keep them for later
        updates.google_review_link = googleReviewLink;
        updates.dummy_page_message = dummyPageMessage;
      }

      if (mode === 'direct') {
        if (!reviewFilterEnabled) {
            // Only validate standard URL if filter is OFF
            const validUrl = validateUrl(url);
            if (!validUrl) throw new Error("Please enter a valid URL.");
            updates.redirect_url = validUrl;
        } else {
            // If filter is ON, the google link is the main destination effectively
            updates.redirect_url = updates.google_review_link;
        }
        updates.link_type = 'direct';
      } else {
        if (!profile.company.trim()) throw new Error("Company Name is required.");
        const activeLinks = profile.links.filter(l => l.label && l.url).map(l => ({ ...l, url: validateUrl(l.url) }));
        if (activeLinks.length === 0) throw new Error("Please add at least one valid link.");
        
        updates.link_type = 'smart';
        updates.company_name = profile.company;
        updates.logo_url = profile.logoUrl;
        updates.bio = profile.bio;
        
        // Sync links
        await db.syncProfileLinks(state.tag_code, activeLinks);
      }

      await db.update(state.id, updates);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 pb-20">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-zinc-800 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiEdit2} className="text-xl text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Update Link</h1>
            <p className="text-zinc-400 text-sm">Modify where your card redirects.</p>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="bg-zinc-900 p-1 rounded-xl flex border border-zinc-800">
          <button 
            onClick={() => setMode('direct')}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${mode === 'direct' ? 'bg-zinc-800 text-white shadow' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            Direct Link
          </button>
          <button 
            onClick={() => setMode('smart')}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${mode === 'smart' ? 'bg-zinc-800 text-white shadow' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            Smart Profile
          </button>
        </div>

        {mode === 'direct' ? (
          <div className="space-y-6">
            
            {/* Negative Review Filter Toggle Section */}
            <div className="bg-amber-500/10 border border-amber-500/20 p-5 rounded-2xl space-y-4">
               <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                     <h3 className="text-amber-500 font-bold flex items-center gap-2">
                        <SafeIcon icon={FaStar} /> Negative Review Filter
                     </h3>
                     <p className="text-xs text-zinc-400 mt-1">
                        Capture 1-3 star ratings privately. Only redirect 4-5 star ratings to your public review page.
                     </p>
                  </div>
                  <button 
                    onClick={() => setReviewFilterEnabled(!reviewFilterEnabled)}
                    className="text-2xl text-amber-500 transition-colors"
                  >
                     <SafeIcon icon={reviewFilterEnabled ? FiCheckSquare : FiSquare} />
                  </button>
               </div>

               {reviewFilterEnabled && (
                  <div className="space-y-4 pt-2 animate-in fade-in slide-in-from-top-2">
                     <Input 
                        label="Google Review Link (Public)" 
                        placeholder="https://g.page/r/..." 
                        value={googleReviewLink} 
                        onChange={(e) => setGoogleReviewLink(e.target.value)} 
                     />
                     <div className="space-y-1.5">
                       <label className="text-sm font-medium text-zinc-400 ml-1">Message for 1-3 Star Ratings (Optional)</label>
                       <textarea 
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-amber-500 focus:ring-amber-500/20 rounded-xl px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-4 transition-all duration-200 min-h-[100px]"
                          placeholder="Thank you for your honest feedback. We'll work on improving!"
                          value={dummyPageMessage}
                          onChange={(e) => setDummyPageMessage(e.target.value)}
                       />
                     </div>
                  </div>
               )}
            </div>

            {!reviewFilterEnabled && (
               <div className="space-y-6 animate-in fade-in">
                  <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                    {[
                      { icon: FaFacebook, color: 'text-blue-500', prefix: 'facebook.com/' },
                      { icon: FaInstagram, color: 'text-pink-500', prefix: 'instagram.com/' },
                      { icon: FaWhatsapp, color: 'text-green-500', prefix: 'wa.me/' },
                      { icon: FaGoogle, color: 'text-white', prefix: 'google.com' },
                    ].map((item, i) => (
                      <button 
                        key={i} 
                        onClick={() => setUrl(item.prefix)}
                        className="flex-shrink-0 w-12 h-12 bg-zinc-900 border border-zinc-800 rounded-full flex items-center justify-center hover:border-zinc-600 transition-colors"
                      >
                        <SafeIcon icon={item.icon} className={`text-xl ${item.color}`} />
                      </button>
                    ))}
                  </div>
                  <Input 
                     label="Destination URL" 
                     value={url} 
                     onChange={(e) => {
                       setUrl(e.target.value);
                       setError('');
                     }} 
                     error={error} 
                  />
               </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-4">
              <Input label="Company Name" value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} />
              <Input label="Logo URL (Optional)" value={profile.logoUrl || ''} onChange={(e) => setProfile({ ...profile, logoUrl: e.target.value })} placeholder="https://..." />
              <Input label="Bio" value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} />
            </div>
            
            <div>
              <label className="text-sm font-medium text-zinc-400 mb-2 block">Links (Max 6)</label>
              <div className="space-y-2">
                {profile.links.map((link, i) => (
                  <div key={i} className="flex gap-2 items-center bg-zinc-900/30 p-2 rounded-lg border border-zinc-800">
                    <span className="text-zinc-600 text-xs w-4">{i + 1}</span>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                       <input 
                         className="bg-transparent text-sm text-white px-2 py-1 outline-none border-b border-transparent focus:border-red-500" 
                         placeholder="Label"
                         value={link.label}
                         onChange={(e) => updateLink(i, 'label', e.target.value)}
                       />
                       <input 
                         className="bg-transparent text-xs text-zinc-400 px-2 py-1 outline-none border-b border-transparent focus:border-red-500 font-mono" 
                         placeholder="URL"
                         value={link.url}
                         onChange={(e) => updateLink(i, 'url', e.target.value)}
                       />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && <div className="text-center p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-xs flex items-center justify-center gap-2"><SafeIcon icon={FiAlertTriangle} /> {error}</div>}
        
        <Button onClick={handleSave} className={success ? "bg-green-600" : ""} disabled={isSaving}>
          {success ? "Saved!" : (isSaving ? "Saving..." : "Save Changes")}
        </Button>
      </div>
    </Layout>
  );
};

export default EditSetup;